import {useCallback, useEffect, useRef} from 'react';

import {BaseFunction} from '../types/BaseFunction';

const getRemainingTime = (lastTriggeredTime: number, throttleMs: number) => {
  const elapsedTime = Date.now() - lastTriggeredTime;
  const remainingTime = throttleMs - elapsedTime;

  return remainingTime < 0 ? 0 : remainingTime;
};

export interface ThrottleOptions {
  /**
   * Execute callback on the leading edge (first call).
   * @default true
   */
  leading?: boolean;
  /**
   * Execute callback on the trailing edge (after throttle period with latest args).
   * @default true
   */
  trailing?: boolean;
}

/**
 * Creates a throttled version of a callback function that limits how often it can be invoked.
 * By default, executes on both leading edge (immediately on first call) and trailing edge
 * (after throttle period with latest arguments), similar to Lodash throttle behavior.
 *
 * @template T - A function type that extends from `BaseFunction`.
 * @param {T} callbackFn - The callback function to throttle.
 * @param {number} throttleMs - The time, in milliseconds, to throttle calls.
 * @param {ThrottleOptions} options - Configuration for leading/trailing edge execution.
 * @returns {T} A throttled version of `callbackFn`.
 *
 * @example
 * // Execute immediately and at the end (default)
 * const handleScroll = useThrottledCallback((e) => {
 *   console.log('Scroll event', e);
 * }, 100);
 *
 * @example
 * // Execute only immediately (leading edge only)
 * const handleClick = useThrottledCallback(
 *   () => console.log('Clicked'),
 *   1000,
 *   {leading: true, trailing: false}
 * );
 *
 * @example
 * // Execute only at the end (trailing edge only)
 * const handleInput = useThrottledCallback(
 *   (value) => search(value),
 *   500,
 *   {leading: false, trailing: true}
 * );
 */
export function useThrottledCallback<T extends BaseFunction>(
  callbackFn: T,
  throttleMs: number,
  options: ThrottleOptions = {}
): T {
  const {leading = true, trailing = true} = options;

  const lastTriggered = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestArgsRef = useRef<Parameters<T> | undefined>(undefined);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  return useCallback(
    (...args: Parameters<T>) => {
      latestArgsRef.current = args;
      const remainingTime = getRemainingTime(lastTriggered.current, throttleMs);

      if (remainingTime === 0) {
        lastTriggered.current = Date.now();

        if (leading) {
          callbackFn(...args);
        }

        if (!leading && trailing && !timeoutRef.current) {
          timeoutRef.current = setTimeout(() => {
            lastTriggered.current = Date.now();
            if (latestArgsRef.current) {
              callbackFn(...latestArgsRef.current);
            }
            cancel();
          }, throttleMs);
        }
      } else if (trailing && !timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          lastTriggered.current = 0;
          if (latestArgsRef.current) {
            callbackFn(...latestArgsRef.current);
          }
          cancel();
        }, remainingTime);
      }
    },
    [callbackFn, cancel, throttleMs, leading, trailing]
  ) as T;
}
